import { Trans, useLingui } from '@lingui/react/macro';
import {
  TrashIcon,
  PencilSquareIcon,
  MinusIcon,
  PlusIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { Button, cn } from '@pantry/ui';
import type { BatchDraftInstance } from '@pantry/shared';

interface BatchInstanceRowProps {
  /** The instance to display */
  instance: BatchDraftInstance;
  /** Index in the list (for display) */
  index: number;
  /** Called when the edit button is pressed */
  onEdit: () => void;
  /** Called when the delete button is pressed */
  onDelete: () => void;
  /** Called when quantity + is pressed */
  onQuantityUp: () => void;
  /** Called when quantity - is pressed */
  onQuantityDown: () => void;
}

/**
 * Format a date string for display using user's locale (e.g., "Dec 15").
 */
function formatDate(dateString: string, locale: string): string {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Compact row component for displaying an individual instance within an expanded GroupedBatchItemCard.
 * Shows expiry date, quantity stepper, and edit/delete actions.
 */
export function BatchInstanceRow({
  instance,
  index,
  onEdit,
  onDelete,
  onQuantityUp,
  onQuantityDown,
}: BatchInstanceRowProps) {
  const { t, i18n } = useLingui();

  return (
    <div
      className={cn(
        'flex items-center gap-3 py-2.5 px-3 bg-muted/30',
        index > 0 && 'border-t border-border/30'
      )}
    >
      {/* Batch indicator */}
      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-card border border-border/50 flex items-center justify-center">
        <span className="text-[11px] font-semibold text-muted-foreground">{index + 1}</span>
      </div>

      {/* Instance info */}
      <div className="flex-1 min-w-0">
        {instance.expiryDate ? (
          <div className="flex items-center gap-1.5">
            <CalendarIcon className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="text-sm text-foreground">
              <Trans>Exp: {formatDate(instance.expiryDate, i18n.locale)}</Trans>
            </span>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground italic">
            <Trans>No expiry set</Trans>
          </span>
        )}
        {instance.notes && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">{instance.notes}</p>
        )}
      </div>

      {/* Quantity stepper */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-7 p-0 rounded-full bg-card"
          onClick={onQuantityDown}
          disabled={instance.quantity <= 1}
        >
          <MinusIcon className="h-3 w-3" />
        </Button>
        <span className="w-8 text-center text-sm font-semibold">{instance.quantity}</span>
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-7 p-0 rounded-full bg-card"
          onClick={onQuantityUp}
        >
          <PlusIcon className="h-3 w-3" />
        </Button>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 rounded-lg hover:bg-card"
          onClick={onEdit}
          title={t`Edit batch`}
        >
          <PencilSquareIcon className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 rounded-lg text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={onDelete}
          title={t`Remove batch`}
        >
          <TrashIcon className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
